import { api } from './http';
import type {
  WhitelistedStudyFile,
  WhitelistedStudyFilesFilters,
  WhitelistedStudyFilesResponse,
  WhitelistedStudyFileUpload,
  WhitelistedStudyFileVersionsResponse,
} from '@/types/whitelistedStudyFiles';
import { API_ENDPOINTS } from '@/config/constants';

// Whitelisted Study Files API functions
// Note: Created for API completeness but not actively used in UI
// as per business requirements - file-upload section doesn't show uploaded files

export async function getWhitelistedStudyFiles(
  filters?: WhitelistedStudyFilesFilters
): Promise<WhitelistedStudyFilesResponse> {
  try {
    const params = new URLSearchParams();

    if (filters?.page) {
      params.append('page', filters.page.toString());
    }

    if (filters?.per_page) {
      params.append('per_page', filters.per_page.toString());
    }

    if (filters?.order) {
      params.append('order', filters.order);
    }

    if (filters?.direction) {
      params.append('direction', filters.direction);
    }

    const url = `${API_ENDPOINTS.WHITELISTED_STUDY_FILES}${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get(url);

    // Extract data from the response structure
    if (
      response.data &&
      response.data.data &&
      Array.isArray(response.data.data) &&
      response.data.pagination
    ) {
      return {
        data: response.data.data,
        pagination: response.data.pagination,
      };
    }

    // Fallback for direct array response
    if (Array.isArray(response.data)) {
      return {
        data: response.data,
        pagination: {
          current_page: 1,
          next_page: null,
          prev_page: null,
          total_pages: 1,
          total_count: response.data.length,
          per_page: response.data.length,
        },
      };
    }

    return {
      data: [],
      pagination: {
        current_page: 1,
        next_page: null,
        prev_page: null,
        total_pages: 0,
        total_count: 0,
        per_page: 10,
      },
    };
  } catch (_error: unknown) {
    throw new Error('Failed to fetch whitelisted study files');
  }
}

export async function uploadWhitelistedStudyFile(
  uploadData: WhitelistedStudyFileUpload
): Promise<WhitelistedStudyFile> {
  try {
    const formData = new FormData();
    formData.append('file', uploadData.file);

    const response = await api.post(
      API_ENDPOINTS.WHITELISTED_STUDY_FILES,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  } catch (error: unknown) {
    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'data' in error.response &&
      error.response.data &&
      typeof error.response.data === 'object' &&
      'error' in error.response.data
    ) {
      const errorData = error.response.data.error;

      // Handle nested error object structure
      if (typeof errorData === 'object' && errorData !== null) {
        const { title, details } = errorData as {
          title?: string;
          details?: string;
        };
        if (title && details) {
          throw new Error(`${title}: ${details}`);
        } else if (title) {
          throw new Error(title);
        } else if (details) {
          throw new Error(details);
        } else {
          throw new Error('Upload failed');
        }
      } else if (typeof errorData === 'string') {
        throw new Error(errorData);
      }
    }
    throw new Error('Failed to upload whitelisted study file');
  }
}

export async function deleteWhitelistedStudyFile(
  fileId: number
): Promise<void> {
  try {
    await api.delete(`${API_ENDPOINTS.WHITELISTED_STUDY_FILES}/${fileId}`);
  } catch (_error: unknown) {
    throw new Error('Failed to delete whitelisted study file');
  }
}

export async function getWhitelistedStudyFileVersions(
  fileId: number
): Promise<WhitelistedStudyFileVersionsResponse> {
  try {
    const response = await api.get(
      `${API_ENDPOINTS.WHITELISTED_STUDY_FILES}/${fileId}/versions`
    );
    return response.data;
  } catch (error: unknown) {
    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'data' in error.response &&
      error.response.data &&
      typeof error.response.data === 'object' &&
      'error' in error.response.data &&
      typeof error.response.data.error === 'string'
    ) {
      throw new Error(error.response.data.error);
    }
    throw new Error('Failed to fetch whitelisted study file versions');
  }
}

// Validation metadata function
export async function validateWhitelistedStudyFilesMeta(
  metadata: Array<{
    filename: string;
    size: number;
    content_type: string;
  }>,
  options?: {
    signal?: AbortSignal;
  }
): Promise<
  Array<{
    filename: string;
    error?: string;
  }>
> {
  try {
    const response = await api.post(
      API_ENDPOINTS.WHITELISTED_STUDY_FILES_VALIDATE_META,
      {
        metadata,
      },
      {
        signal: options?.signal,
      }
    );

    // Extract data from the response structure
    if (
      response.data &&
      response.data.data &&
      Array.isArray(response.data.data)
    ) {
      return response.data.data;
    }

    // Fallback for direct array response
    if (Array.isArray(response.data)) {
      return response.data;
    }

    return [];
  } catch (error: unknown) {
    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'data' in error.response &&
      error.response.data &&
      typeof error.response.data === 'object' &&
      'error' in error.response.data &&
      typeof error.response.data.error === 'string'
    ) {
      throw new Error(error.response.data.error);
    }
    throw new Error('Failed to validate whitelisted study files metadata');
  }
}
